import { ReactNode, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import dayjs from 'dayjs';
import Label from '@/components/fields/Label';
import Days from '@/components/fields/DaysField';
import Months from '@/components/fields/MonthsField';
import Years from '@/components/fields/YearsField';

export default function DateField({
  id,
  children,
  isRequired,
  type = 'normal',
  skipYears = 15,
}: {
  id: string;
  children: ReactNode;
  isRequired: boolean;
  type?: 'future' | 'normal';
  skipYears?: number;
}): JSX.Element {
  const { watch, setError, clearErrors, errors, trigger } = useFormContext();
  const [errorName, setErrorName] = useState('');
  const formType = watch('form_type');
  const step = watch('step');

  let ageDiff = 0;
  let expiryDiff = 0;
  let passportExpiryDateDiff = 0;
  const ALLOWED_AGE_MONTHS = 837; // 69 Years & 9 months

  const birthDay = watch('date_of_birth_day');
  const birthMonth = watch('date_of_birth_month');
  const birthYear = watch('date_of_birth_year');

  const licenceDay = watch('licence_expiry_date_day');
  const licenceMonth = watch('licence_expiry_date_month');
  const licenceYear = watch('licence_expiry_date_year');

  const passportExpiryDay = watch('passport_expiry_date_day');
  const passportExpiryMonth = watch('passport_expiry_date_month');
  const passportExpiryYear = watch('passport_expiry_date_year');

  useEffect(() => {
    const now = dayjs();
    const isBirthDate = birthDay && birthMonth && birthYear;
    const isLicenceExpiry = licenceDay && licenceMonth && licenceYear;
    const isPassportExpiryDate =
      passportExpiryDay && passportExpiryMonth && passportExpiryYear;

    if (passportExpiryDay || passportExpiryMonth || passportExpiryYear) {
      trigger([
        'passport_expiry_date_day',
        'passport_expiry_date_month',
        'passport_expiry_date_year',
      ]);
    }

    if (['renewal', 'exchange'].includes(formType) && step === '4') {
      if (isPassportExpiryDate) {
        let expiryDate = dayjs(
          `${passportExpiryYear}-${passportExpiryMonth}-${passportExpiryDay}`
        );
        expiryDate = dayjs(expiryDate);
        passportExpiryDateDiff = expiryDate.diff(now, 'day');

        if (passportExpiryDateDiff < -30) {
          setErrorName('passportExpiryDateDiff');
          setError('passportExpiryDateDiff', {
            type: 'manual',
            message: 'Your passport is not valid anymore',
          });
        } else {
          clearErrors('passportExpiryDateDiff');
        }
      } else {
        setErrorName('passportExpiryDateDiff');
        setError('passportExpiryDateDiff', { type: 'manual' });
      }
    }

    if (birthDay || birthMonth || birthYear) {
      trigger([
        'date_of_birth_day',
        'date_of_birth_month',
        'date_of_birth_year',
      ]);
    }

    if (isBirthDate && formType == 'renewal-old') {
      const birthDate = dayjs(`${birthYear}-${birthMonth}-${birthDay}`);
      ageDiff = now.diff(birthDate, 'month');
      if (ageDiff < ALLOWED_AGE_MONTHS) {
        setErrorName('ageDiff');
        setError('ageDiff', {
          type: 'manual',
          message:
            'You can use this service if you’re 70 or over, or will be 70 in the next 90 days',
        });
      } else {
        clearErrors('ageDiff');
      }
    }

    if (isLicenceExpiry) {
      trigger([
        'licence_expiry_date_day',
        'licence_expiry_date_month',
        'licence_expiry_date_year',
      ]);
      const expiryDate = dayjs(`${licenceYear}-${licenceMonth}-${licenceDay}`);
      expiryDiff = now.diff(expiryDate, 'day');
      if (expiryDiff < 0 && Math.abs(expiryDiff) > 90) {
        setErrorName('expiryDiff');
        setError('expiryDiff', {
          type: 'manual',
          message:
            'You driver’s licence must have less than 90 days before expiration',
        });
      } else {
        clearErrors('expiryDiff');
      }
    }
  }, [
    formType,
    step,
    birthDay,
    birthMonth,
    birthYear,
    licenceDay,
    licenceMonth,
    licenceYear,
    passportExpiryDay,
    passportExpiryMonth,
    passportExpiryYear,
  ]);

  return (
    <div className="mt-4 md:flex md:items-center">
      <div className="md:w-2/5 md:pr-4">
        <Label id={id} isRequired={isRequired}>
          {children}
        </Label>
      </div>
      <div className="md:w-2/5 md:pl-4">
        <div className="mt-1 md:flex">
          <Days id={`${id}_day`} className="md:w-1/3 md:pr-2" />
          <Months id={`${id}_month`} className="md:w-1/3 md:px-1" />
          <Years
            id={`${id}_year`}
            className="md:w-1/3 md:pl-2"
            type={type}
            skipYears={skipYears}
          />
        </div>
        {errors[errorName] && (
          <div className="-mt-2 mb-2 font-medium text-sm text-red-600">
            {errors[errorName].message}
          </div>
        )}
      </div>
    </div>
  );
}
